import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/context/WorkspaceContext';
import { searchAPI, SearchResult } from '@/services/api/search';
import { NavigationItem, NavigationSection, SECTION_LABELS } from './navigation';

interface CommandMenuProps {
  items: NavigationItem[];
  open: boolean;
  onClose: () => void;
}

const CommandMenu: React.FC<CommandMenuProps> = ({ items, open, onClose }) => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recordResults, setRecordResults] = useState<SearchResult[]>([]);
  const [recordState, setRecordState] = useState<'idle' | 'loading' | 'error'>('idle');
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const results = useMemo(() => items.filter((item) => {
    if (!normalizedQuery) return true;
    const searchableText = [item.label, SECTION_LABELS[item.section], ...item.keywords]
      .join(' ')
      .toLocaleLowerCase();
    return searchableText.includes(normalizedQuery);
  }), [items, normalizedQuery]);

  const groups = useMemo(() => results.reduce<Array<{ section: NavigationSection; items: NavigationItem[] }>>(
    (accumulator, item) => {
      const existing = accumulator.find((group) => group.section === item.section);
      if (existing) existing.items.push(item);
      else accumulator.push({ section: item.section, items: [item] });
      return accumulator;
    },
    [],
  ), [results]);
  const visibleRecordResults = useMemo(() => normalizedQuery.length >= 3 ? recordResults : [], [normalizedQuery.length, recordResults]);
  const orderedResults = useMemo(() => [
    ...groups.flatMap((group) => group.items).map(item => ({ id: `page-${item.id}`, label: item.label, href: item.href, page: item })),
    ...visibleRecordResults.map(record => ({ id: `record-${record.kind}-${record.id}`, label: record.title, href: record.href, record })),
  ], [groups, visibleRecordResults]);
  const activeResult = orderedResults[activeIndex];

  useEffect(() => {
    if (!open || normalizedQuery.length < 3 || !currentWorkspace?.id) {
      setRecordResults([]);
      setRecordState('idle');
      return undefined;
    }
    const controller = new AbortController();
    setRecordState('loading');
    const timer = window.setTimeout(() => {
      searchAPI.search(currentWorkspace.id, query.trim(), controller.signal).then(response => {
        if (!controller.signal.aborted) { setRecordResults(response.data); setRecordState('idle'); }
      }).catch(() => {
        if (!controller.signal.aborted) { setRecordResults([]); setRecordState('error'); }
      });
    }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [currentWorkspace?.id, normalizedQuery, open, query]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [query, open]);

  useEffect(() => {
    if (!activeResult) return;
    document.getElementById(`command-result-${activeResult.id}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeResult]);

  const select = (href: string) => {
    navigate(href);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!orderedResults.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % orderedResults.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (
        current <= 0 || current >= orderedResults.length ? orderedResults.length - 1 : current - 1
      ));
    } else if (event.key === 'Enter' && activeResult) {
      event.preventDefault();
      select(activeResult.href);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} initialFocus={inputRef} className="relative z-50">
      <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-start justify-center overflow-y-auto p-4 pt-[12vh] sm:p-6 sm:pt-[16vh]">
        <DialogPanel className="w-full max-w-2xl overflow-hidden rounded-2xl border border-amber-400/20 bg-white shadow-2xl dark:bg-slate-900">
          <DialogTitle className="sr-only">Command menu</DialogTitle>
          <div className="flex items-center gap-3 border-b border-slate-200 px-4 dark:border-slate-700">
            <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
               placeholder="Search pages and records..."
               aria-label="Search pages and records"
              aria-controls="command-menu-results"
               aria-activedescendant={activeResult ? `command-result-${activeResult.id}` : undefined}
              className="min-h-14 w-full bg-transparent px-2 text-base text-slate-950 outline-none placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-club-gold-600 dark:text-white dark:focus-visible:ring-club-gold-500"
            />
          </div>
          <div id="command-menu-results" className="max-h-[55vh] overflow-y-auto p-2">
            {groups.map((group) => (
              <section key={group.section} aria-labelledby={`command-section-${group.section}`} className="mb-2 last:mb-0">
                <h2 id={`command-section-${group.section}`} className="px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                  {SECTION_LABELS[group.section]}
                </h2>
                {group.items.map((item) => {
                  const resultId = `page-${item.id}`;
                  const index = orderedResults.findIndex(result => result.id === resultId);
                  return (
                    <button
                      id={`command-result-${resultId}`}
                      key={item.id}
                      type="button"
                      tabIndex={-1}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => select(item.href)}
                      className={`flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-gold-600 dark:focus-visible:ring-club-gold-500 ${
                        activeIndex === index
                          ? 'bg-slate-900 text-amber-300 dark:bg-amber-400 dark:text-slate-950'
                          : 'text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </section>
            ))}
            {normalizedQuery.length >= 3 && <section aria-labelledby="command-section-records" className="mb-2">
              <h2 id="command-section-records" className="px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Records</h2>
              {visibleRecordResults.map(record => {
                const resultId = `record-${record.kind}-${record.id}`;
                const index = orderedResults.findIndex(result => result.id === resultId);
                return <button id={`command-result-${resultId}`} key={resultId} type="button" tabIndex={-1} onMouseEnter={() => setActiveIndex(index)} onClick={() => select(record.href)} className={`flex min-h-11 w-full min-w-0 flex-col justify-center rounded-lg px-3 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-gold-600 dark:focus-visible:ring-club-gold-500 ${activeIndex === index ? 'bg-slate-900 text-amber-300 dark:bg-amber-400 dark:text-slate-950' : 'text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800'}`}><span className="break-words font-semibold">{record.title}</span><span className="break-words text-xs opacity-75">{record.description}</span></button>;
              })}
              {recordState === 'loading' && <p role="status" className="px-3 py-3 text-sm text-slate-600 dark:text-slate-300">Searching permitted records...</p>}
              {recordState === 'error' && <p role="status" className="px-3 py-3 text-sm text-amber-800 dark:text-amber-200">Record search is unavailable. Page destinations remain available.</p>}
            </section>}
            {!results.length && !visibleRecordResults.length && recordState !== 'loading' && (
              <p className="px-4 py-10 text-center text-sm text-slate-600 dark:text-slate-300">
                No permitted destinations match your search.
              </p>
            )}
          </div>
          <div role="status" aria-live="polite" className="border-t border-slate-200 px-4 py-2 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
             {orderedResults.length} {orderedResults.length === 1 ? 'result' : 'results'}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default CommandMenu;
